import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { IoChevronUpOutline, IoChevronDownOutline, IoCloseOutline } from "react-icons/io5";
import HeaderSection from "./HeaderSection/HeaderSection";
import BottomTypeArea from "./BottomTypeArea/BottomTypeArea";
import NotFound from "../../../Components/NoChats/NoChats";
import { useChatSettings } from "../../../Context/ChatSettingsContext";
import ChatsSection from "../../../Pages/Home/ChatsSection/ChatsSection";
import NoChats from "../../../Components/NoChats/NoChats";
import { FadeLoader } from "react-spinners";

export default function ChatingSection({
  isNewChannel,
  setActiveSection,
  setShowProfileView,
  showGroupSection,
  chatConversationId,
  activeChat,
  allMessages,
  setAllMessages,
  isMessageLoading,
  sendMessage,
  sendFilesInMessage,
  getSecretKey,
  settings,
  showAddMenu,
  setShowAddMenu,
  scrollfetchingChatData,
  hasMoreMessage,
  setChatUsers,
  typingUsers,
  socket,
  userInfo,
}) {
  const chatContainerRef = useRef(null);
  const location = useLocation();
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Local Chat Search State
  const [showLocalSearch, setShowLocalSearch] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [searchStats, setSearchStats] = useState({ current: 0, total: 0 });
  const [triggerNext, setTriggerNext] = useState(0);
  const [triggerPrev, setTriggerPrev] = useState(0);
  const [isDecryptionLoading, setIsDecryptionLoading] = useState(false);

  useEffect(() => {
    setIsDecryptionLoading(true);
    if (location.state?.activeMessageId) {
      setActiveMessageId(location.state.activeMessageId);
      // Clear state safely preserving other history props if needed, or use navigate to clear
      // Actually, we just want to execute this ONCE. The state persists on refresh, which we want to avoid re-triggering?
      // But clearing it here modifies history. Let's just consume it.
      // Better: navigate(".", { replace: true, state: {} }); 
      // But simpler: just preserve structure
      const currentState = window.history.state || {};
      // We can't easily clear just one key from React Router's specific state structure without risk.
      // Let's rely on the fact that we set activeMessageId to null in timeout.
      // And the effect depends on [location.state]. 
      // If we don't clear it, refresh might re-trigger. 
      // For now, PRESERVE logic is safest.
      window.history.replaceState({ ...currentState, usr: { ...currentState.usr, activeMessageId: null } }, document.title);
    }
  }, [chatConversationId, location.state]);

  // Deep Link Scroll Effect (still useful for global deep link)
  useEffect(() => {
    if (activeMessageId && !loading && !isMessageLoading && allMessages.length > 0) {
      const element = document.getElementById(activeMessageId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Remove highlight after 2 seconds
        setTimeout(() => {
          setActiveMessageId(null);
        }, 3000);
      }
    }
  }, [activeMessageId, loading, isMessageLoading, allMessages]);

  useEffect(() => {
    const container = chatContainerRef.current;

    const handleScroll = async () => {
      if (!container || loading || !hasMoreMessage) return;
      const isAtBottom =
        Math.abs(
          container.scrollTop + container.scrollHeight - container.clientHeight
        ) < 1;
      if (isAtBottom) {
        setLoading(true);
        await scrollfetchingChatData();
        setLoading(false);
      }
    };

    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loading, scrollfetchingChatData]);

  const bgColor = useMemo(() => {
    return isMessageLoading ? `bg-zinc-100 dark:bg-slate-950` : `bg-[${settings?.background}] dark:bg-slate-950`;
  }, [settings, isMessageLoading]);

  return (
    <div className="flex flex-col flex-1 h-full">
      {activeChat ? (
        <>
          <HeaderSection
            isNewChannel={isNewChannel}
            showGroupSection={showGroupSection}
            setShowProfileView={setShowProfileView}
            setActiveSection={setActiveSection}
            activeChat={activeChat}
            onToggleSearch={() => setShowLocalSearch(!showLocalSearch)}
          />
          {showLocalSearch && (
            <div className="sticky top-[70px] mx-6 z-10 flex items-center justify-between bg-white dark:bg-slate-800 p-2 rounded-xl shadow-sm border border-zinc-200 dark:border-slate-700 mt-2 gap-2">
              <input
                autoFocus
                type="text"
                placeholder="Search in chat..."
                className="flex-1 bg-transparent border-none outline-none text-sm text-zinc-800 dark:text-zinc-100 px-2"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setTriggerNext(prev => prev + 1);
                  }
                }}
              />
              <span className="text-xs text-zinc-500 whitespace-nowrap px-2 border-r border-zinc-200 dark:border-slate-700">
                {searchStats.total > 0 ? `${searchStats.current + 1} of ${searchStats.total}` : "No matches"}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setTriggerPrev(prev => prev + 1)}
                  className="p-1 hover:bg-zinc-100 dark:hover:bg-slate-700 rounded-lg text-zinc-600 dark:text-zinc-300 disabled:opacity-50"
                  disabled={searchStats.total === 0}
                >
                  <IoChevronUpOutline />
                </button>
                <button
                  onClick={() => setTriggerNext(prev => prev + 1)}
                  className="p-1 hover:bg-zinc-100 dark:hover:bg-slate-700 rounded-lg text-zinc-600 dark:text-zinc-300 disabled:opacity-50"
                  disabled={searchStats.total === 0}
                >
                  <IoChevronDownOutline />
                </button>
                <button
                  onClick={() => {
                    setShowLocalSearch(false);
                    setLocalSearchQuery("");
                    setSearchStats({ current: 0, total: 0 });
                  }}
                  className="p-1 hover:bg-red-50 text-red-500 rounded-lg ml-1"
                >
                  <IoCloseOutline />
                </button>
              </div>
            </div>
          )}
          <main
            className={`flex-1 ${showAddMenu ? "overflow-hidden" : "overflow-y-auto"
              } px-0 pb-0 flex flex-col-reverse ${bgColor} ${allMessages.length > 0 ? "pl-4 pb-4" : "px-0"
              } `}
            ref={chatContainerRef}
          >
            {!isMessageLoading && (
              <ChatsSection
                activeChat={activeChat}
                isDecryptionLoading={isDecryptionLoading}
                setIsDecryptionLoading={setIsDecryptionLoading}
                allMessages={allMessages}
                setAllMessages={setAllMessages}
                getSecretKey={getSecretKey}
                settings={settings}
                chatConversationId={chatConversationId}
                isMessageLoading={isMessageLoading}
                setChatUsers={setChatUsers}
                isTyping={typingUsers?.has(activeChat?.user?._id)}
                socket={socket}
                activeMessageId={activeMessageId}
                setActiveMessageId={setActiveMessageId}
                localSearchQuery={localSearchQuery}
                triggerNext={triggerNext}
                triggerPrev={triggerPrev}
                setSearchStats={setSearchStats}
              />
            )}
            {!isMessageLoading && allMessages.length === 0 && <NoChats />}
            {loading && (
              <div className="text-center text-sm text-gray-400 py-2">
                Loading messages...
              </div>
            )}
          </main>
          {!isNewChannel && !activeChat?.user?.isBlocked ? (
            <footer className="w-full min-h-[80px] bg-transparent flex items-center justify-between text-black pt-4 text-center">
              <BottomTypeArea
                onScheduleClick={() => {
                  setActiveSection("schedule");
                  setShowProfileView(true);
                }}
                activeChat={activeChat}
                sendMessage={sendMessage}
                sendFilesInMessage={sendFilesInMessage}
                showAddMenu={showAddMenu}
                setShowAddMenu={setShowAddMenu}
                socket={socket}
                chatConversationId={chatConversationId}
                userInfo={userInfo}
                getSecretKey={getSecretKey}
              />
            </footer>
          ) : (
            <footer className="min-h-[80px] w-full bg-transparent flex items-center justify-center text-black p-4 text-center">
              You have blocked this user, you cannot send message to this user
            </footer>
          )}
        </>
      ) : (
        <NotFound />
      )}
    </div>
  );
}
